/**
 * Globe Context — conversational state engine for EarthVoice.
 *
 * Tracks geographic focus, active layers, highlighted features, last result,
 * and conversation history. Travels with every AI request so Claude can
 * resolve references like "here", "those", "color them by density".
 *
 * Chain model: a sequence of related queries forms a "chain". When the
 * student switches countries or says "start over", a new chain begins.
 */

// ── Helpers ──────────────────────────────────────────────────
function generateId() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// ── State ────────────────────────────────────────────────────
const SESSION_ID = generateId();
let chainId = generateId();
let chainDepth = 1;
let queryIndex = 0;

const geographicFocus = {
  country: null,       // ISO-3 code (e.g. 'JPN')
  country_name: null,  // human name (e.g. 'Japan')
  admin_level: null,   // 'ADM1', 'ADM2', etc.
  admin_unit: null,    // e.g. 'Kyoto'
  bbox: null,          // [west, south, east, north]
};

const camera = {
  center: [0, 20],
  zoom: 1.5,
  pitch: 0,
  bearing: 0,
};

let activeLayers = [];
// Each layer: { id, type, dataset, attribute, featureCount, addedAt }

let lastResult = null;
// { type, featureCount, topValues, dataset }

const conversationHistory = [];
// { role, content }[] — kept to 10 entries (5 exchanges)
const MAX_HISTORY = 10;

// Branch stack for "branch" context_action (3-deep max)
const branchStack = [];
const MAX_BRANCH_DEPTH = 3;

// ── Public API ───────────────────────────────────────────────

export function getContext() {
  return {
    session_id: SESSION_ID,
    chain_id: chainId,
    chain_depth: chainDepth,
    query_index: queryIndex,
    geographic_focus: { ...geographicFocus },
    camera: { ...camera },
    active_layers: activeLayers.map(l => ({ ...l })),
    last_result: lastResult ? { ...lastResult } : null,
    conversation_history: conversationHistory.slice(),
  };
}

export function getSessionId() {
  return SESSION_ID;
}

export function getChainId() {
  return chainId;
}

export function getChainDepth() {
  return chainDepth;
}

export function getQueryIndex() {
  return queryIndex;
}

export function incrementQueryIndex() {
  queryIndex++;
  return queryIndex;
}

export function updateFocus(country, countryName, adminLevel, adminUnit, bbox) {
  if (country !== undefined) geographicFocus.country = country;
  if (countryName !== undefined) geographicFocus.country_name = countryName;
  if (adminLevel !== undefined) geographicFocus.admin_level = adminLevel;
  if (adminUnit !== undefined) geographicFocus.admin_unit = adminUnit;
  if (bbox !== undefined) {
    if (bbox !== null && (!Array.isArray(bbox) || bbox.length !== 4)) {
      console.warn('[globe-context] updateFocus: bbox must be [west, south, east, north] or null, got:', bbox);
    }
    geographicFocus.bbox = bbox;
  }
}

export function updateCamera(center, zoom, pitch, bearing) {
  if (center !== undefined) {
    if (!Array.isArray(center) || center.length !== 2) {
      console.warn('[globe-context] updateCamera: center must be [lng, lat], got:', center);
    }
    camera.center = center;
  }
  if (zoom !== undefined) {
    if (typeof zoom !== 'number' || zoom < 0 || zoom > 24) {
      console.warn('[globe-context] updateCamera: zoom must be 0–24, got:', zoom);
    }
    camera.zoom = zoom;
  }
  if (pitch !== undefined) camera.pitch = pitch;
  if (bearing !== undefined) camera.bearing = bearing;
}

export function addLayer(layerMeta) {
  // Prevent duplicates by id
  activeLayers = activeLayers.filter(l => l.id !== layerMeta.id);
  activeLayers.push({
    ...layerMeta,
    addedAt: Date.now(),
  });
  // Keep only last 5 layers
  if (activeLayers.length > 5) {
    activeLayers = activeLayers.slice(-5);
  }
}

export function removeLayer(layerId) {
  activeLayers = activeLayers.filter(l => l.id !== layerId);
}

export function setHighlightedFeatures(layerId, features) {
  const layer = activeLayers.find(l => l.id === layerId);
  if (layer) {
    layer.highlightedFeatures = features;
  }
}

export function setLastResult(resultObj) {
  lastResult = resultObj;
}

export function pushHistory(role, content) {
  conversationHistory.push({ role, content });
  while (conversationHistory.length > MAX_HISTORY) {
    conversationHistory.shift();
  }
}

export function resetChain() {
  chainId = generateId();
  chainDepth = 1;
  activeLayers = [];
  lastResult = null;
  // Clear highlighted features but keep geographic_focus for transition context
  // Keep conversation_history for transition context
}

export function advanceChain() {
  chainDepth++;
}

// ── Branch stack (for "branch" context_action) ───────────────

export function pushBranch() {
  if (branchStack.length >= MAX_BRANCH_DEPTH) {
    branchStack.shift(); // Drop oldest if full
  }
  branchStack.push({
    chainId,
    chainDepth,
    geographicFocus: { ...geographicFocus },
    activeLayers: activeLayers.map(l => ({ ...l })),
    lastResult: lastResult ? { ...lastResult } : null,
  });
  // Start a new chain for the branch
  chainId = generateId();
  chainDepth = 1;
  activeLayers = [];
  lastResult = null;
}

export function popBranch() {
  if (branchStack.length === 0) {
    console.warn('[globe-context] popBranch called with empty stack');
    return false;
  }
  const prev = branchStack.pop();
  chainId = prev.chainId;
  chainDepth = prev.chainDepth;
  // Clear all focus properties first, then restore saved state.
  // Object.assign alone won't remove properties added after push.
  geographicFocus.country = null;
  geographicFocus.country_name = null;
  geographicFocus.admin_level = null;
  geographicFocus.admin_unit = null;
  geographicFocus.bbox = null;
  Object.assign(geographicFocus, prev.geographicFocus);
  activeLayers = prev.activeLayers;
  lastResult = prev.lastResult;
  return true;
}

export function hasBranch() {
  return branchStack.length > 0;
}

// ── Serialization ────────────────────────────────────────────

export function serialize() {
  return JSON.stringify(getContext());
}

/**
 * Format context as a human-readable block for the Claude system prompt.
 * Claude understands natural language descriptions of state better than raw JSON.
 */
export function formatForPrompt() {
  const ctx = getContext();
  const parts = [];

  parts.push(`<globe_state>`);

  // Geographic focus
  if (ctx.geographic_focus.country_name) {
    let focus = `The student is currently viewing ${ctx.geographic_focus.country_name}`;
    if (ctx.geographic_focus.admin_unit) {
      focus += `, specifically ${ctx.geographic_focus.admin_unit}`;
    }
    if (ctx.geographic_focus.admin_level) {
      focus += ` (${ctx.geographic_focus.admin_level} level)`;
    }
    focus += '.';
    parts.push(focus);
  } else {
    parts.push('The student is viewing the global view (no specific country focused).');
  }

  // Camera
  parts.push(`Camera: center=[${ctx.camera.center[0].toFixed(1)}, ${ctx.camera.center[1].toFixed(1)}], zoom=${ctx.camera.zoom.toFixed(1)}`);

  // Active layers
  if (ctx.active_layers.length > 0) {
    const layerDescs = ctx.active_layers.map(l => {
      let desc = `${l.type || 'unknown'}`;
      if (l.dataset) desc += ` (${l.dataset})`;
      if (l.attribute) desc += ` colored by ${l.attribute}`;
      if (l.featureCount) desc += ` — ${l.featureCount} features`;
      if (l.highlightedFeatures?.length) {
        desc += ` — highlighted: ${l.highlightedFeatures.slice(0, 3).join(', ')}`;
      }
      return desc;
    });
    parts.push(`Active layers: ${layerDescs.join('; ')}`);
  } else {
    parts.push('No data layers active (clean globe).');
  }

  // Last result
  if (ctx.last_result) {
    let result = `Last query result: ${ctx.last_result.type || 'data'}`;
    if (ctx.last_result.featureCount) result += `, ${ctx.last_result.featureCount} features`;
    if (ctx.last_result.dataset) result += ` from ${ctx.last_result.dataset}`;
    parts.push(result);
  }

  // Chain info
  parts.push(`Chain: depth=${ctx.chain_depth}, query #${ctx.query_index}`);

  parts.push(`</globe_state>`);

  return parts.join('\n');
}

// Default export as a convenience namespace
const globeContext = {
  getContext,
  getSessionId,
  getChainId,
  getChainDepth,
  getQueryIndex,
  incrementQueryIndex,
  updateFocus,
  updateCamera,
  addLayer,
  removeLayer,
  setHighlightedFeatures,
  setLastResult,
  pushHistory,
  resetChain,
  advanceChain,
  pushBranch,
  popBranch,
  hasBranch,
  serialize,
  formatForPrompt,
};

export default globeContext;
